import { useMemo, useState } from "react";
import type { PropertyResearchRepository, ResearchStatus } from "../types";

const COLORS: Record<ResearchStatus, { background: string; color: string }> = {
  confirmed: { background: "#e3f2e8", color: "var(--pass)" },
  active: { background: "#e5eef0", color: "var(--forest)" },
  expired: { background: "#eee9e2", color: "var(--muted)" },
  verify: { background: "#f6edd8", color: "var(--brass-deep)" },
  concept: { background: "#eee9f6", color: "#5d4778" },
};

export function DevelopmentResearchRepository({
  research,
  onChange,
}: {
  research: PropertyResearchRepository;
  onChange: (research: PropertyResearchRepository) => void;
}) {
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState("");
  const needle = query.trim().toLowerCase();
  const documents = useMemo(() => research.documents.filter((doc) => !needle || [doc.title, doc.agency, doc.category, doc.summary].join(" ").toLowerCase().includes(needle)), [research.documents, needle]);

  const addNote = () => {
    const text = draft.trim();
    if (!text) return;
    onChange({
      ...research,
      notes: [{ id: `note-${Date.now()}`, date: new Date().toISOString().slice(0, 10), text }, ...research.notes],
    });
    setDraft("");
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(research, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "west-dixie-research-repository.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  return <>
    <h2 className="sechead" id="sec-dev-research">Property research repository</h2>
    <div className="pe">
      <div className="l">◆ Current evidence read · updated {research.updated}</div>
      <p><b>{research.headline}.</b> {research.currentRead}</p>
    </div>

    <div className="grid g4">
      <MiniKpi label="Brownfield area" value="CONFIRMED" sub="BF131501000 · R2015-88" tone="good" />
      <MiniKpi label="Cleanup site" value="ACTIVE" sub="BF131501001 · ERIC_9217" tone="warn" />
      <MiniKpi label="Closure order" value="NOT RECORDED" sub="SRCO date blank in current tables" tone="bad" />
      <MiniKpi label="Evidence library" value={`${research.documents.length} records`} sub="local PDFs + official live sources" />
    </div>

    <h3 className="fr" style={{ color: "var(--forest)", margin: "24px 0 10px", fontSize: 21 }}>Designation matrix</h3>
    <div className="card pad"><div className="tablewrap"><table className="t">
      <thead><tr><th>Program / designation</th><th>Status</th><th>Finding</th><th>Development implication</th></tr></thead>
      <tbody>{research.designations.map((item) => <tr key={item.name}>
        <td><b>{item.name}</b></td>
        <td><Status status={item.status} /></td>
        <td>{item.finding}</td>
        <td style={{ color: "var(--muted)" }}>{item.implication}</td>
      </tr>)}</tbody>
    </table></div></div>

    <h3 className="fr" style={{ color: "var(--forest)", margin: "24px 0 10px", fontSize: 21 }}>Complete history</h3>
    <div className="card pad">
      {research.timeline.map((event, i) => <div key={`${event.date}-${event.title}`} style={{ display: "grid", gridTemplateColumns: "115px 18px 1fr", gap: 10, paddingBottom: i === research.timeline.length - 1 ? 0 : 18 }}>
        <div className="eyebrow" style={{ paddingTop: 3 }}>{event.date}</div>
        <div style={{ position: "relative" }}><span style={{ display: "block", width: 10, height: 10, borderRadius: 10, background: "var(--brass)", marginTop: 5 }} />{i < research.timeline.length - 1 && <span style={{ position: "absolute", left: 4, top: 17, bottom: -17, width: 1, background: "var(--cream-300)" }} />}</div>
        <div><b style={{ color: "var(--forest)" }}>{event.title}</b><div className="note" style={{ marginTop: 3, lineHeight: 1.55 }}>{event.detail}</div></div>
      </div>)}
    </div>

    <h3 className="fr" style={{ color: "var(--forest)", margin: "24px 0 10px", fontSize: 21 }}>Funding and incentive workbench</h3>
    <div className="card pad"><div className="tablewrap"><table className="t">
      <thead><tr><th>Opportunity</th><th>Status</th><th>Potential value</th><th>Who qualifies / conditions</th><th>Next action</th></tr></thead>
      <tbody>{research.opportunities.map((item) => <tr key={item.name}>
        <td><b>{item.name}</b></td><td><Status status={item.status} /></td><td>{item.benefit}</td><td style={{ color: "var(--muted)" }}>{item.eligibility}</td><td>{item.action}</td>
      </tr>)}</tbody>
    </table></div></div>

    <h3 className="fr" style={{ color: "var(--forest)", margin: "24px 0 10px", fontSize: 21 }}>What could be built</h3>
    <div className="grid g2">
      {research.buildOptions.map((option) => <div className="card pad" key={option.name}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}><div className="fr" style={{ color: "var(--forest)", fontSize: 19, fontWeight: 600 }}>{option.name}</div><Status status={option.status} /></div>
        <p style={{ marginBottom: 8 }}>{option.program}</p>
        <Line label="Entitlement" text={option.entitlement} />
        <Line label="City value" text={option.cityValue} />
        <Line label="Constraints" text={option.constraints} />
      </div>)}
    </div>
    <div className="flag warn" style={{ marginTop: 12 }}><span>◐</span><div><b>Buildability conclusion:</b> private mixed-use development is not by right today. The options are planning concepts that require a FLUM amendment, CF rezoning, environmental controls, infrastructure confirmation and public approvals.</div></div>

    <div className="grid g2" style={{ marginTop: 20 }}>
      <div className="card pad">
        <div className="eyebrow">How the project can create public return</div>
        <ul style={{ margin: "10px 0 0", paddingLeft: 20, lineHeight: 1.6 }}>{research.cityRevenueChannels.map((item) => <li key={item}>{item}</li>)}</ul>
      </div>
      <div className="card pad">
        <div className="eyebrow">Open diligence - do not underwrite as complete</div>
        <ol style={{ margin: "10px 0 0", paddingLeft: 20, lineHeight: 1.6 }}>{research.openQuestions.map((item) => <li key={item}>{item}</li>)}</ol>
      </div>
    </div>

    <h3 className="fr" style={{ color: "var(--forest)", margin: "24px 0 10px", fontSize: 21 }}>Hard-copy document library</h3>
    <div className="card pad">
      <div style={{ display: "flex", alignItems: "end", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div className="fld" style={{ minWidth: 280, flex: 1 }}><label>Search evidence</label><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Resolution, FDEP, zoning, VCTC..." /></div>
        <button className="btn btn-g btn-sm" onClick={exportJson}>Export research JSON</button>
      </div>
      <div className="grid g2" style={{ marginTop: 14 }}>
        {documents.map((doc) => <div key={doc.id} style={{ border: "1px solid var(--cream-300)", padding: 14, borderRadius: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}><span className="eyebrow">{doc.category} · {doc.date}</span><Status status={doc.status} /></div>
          <div style={{ color: "var(--forest)", fontWeight: 700, marginTop: 6 }}>{doc.title}</div>
          <div className="note" style={{ marginTop: 4 }}>{doc.agency}{doc.pages ? ` · ${doc.pages}` : ""}</div>
          <p style={{ margin: "8px 0", lineHeight: 1.5 }}>{doc.summary}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {doc.localPath && <a className="btn btn-g btn-sm" href={doc.localPath} target="_blank" rel="noreferrer">View file</a>}
            {doc.localPath && <a className="btn btn-sm" href={doc.localPath} download>Download</a>}
            {doc.sourceUrl !== doc.localPath && <a className="btn btn-sm" href={doc.sourceUrl} target="_blank" rel="noreferrer">Official source ↗</a>}
          </div>
        </div>)}
      </div>
    </div>

    <h3 className="fr" style={{ color: "var(--forest)", margin: "24px 0 10px", fontSize: 21 }}>Running research document</h3>
    <div className="card pad">
      <div className="fld"><label>Add a dated research update</label><textarea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Record a new agency response, consultant finding, document request, decision or open question..." /></div>
      <button className="btn btn-g btn-sm" style={{ marginTop: 8 }} onClick={addNote}>Add update</button>
      {research.notes.length === 0 ? <p className="note" style={{ marginBottom: 0 }}>No user updates yet. Added notes stay with this property in the Beacon record and are retained when the evidence library is refreshed.</p> : research.notes.map((note) => <div key={note.id} style={{ borderTop: "1px solid var(--cream-300)", marginTop: 12, paddingTop: 12 }}><span className="eyebrow">{note.date}</span><p style={{ marginBottom: 0 }}>{note.text}</p></div>)}
    </div>
  </>;
}

function Status({ status }: { status: ResearchStatus }) {
  return <span className="tag" style={COLORS[status]}>{status.toUpperCase()}</span>;
}

function Line({ label, text }: { label: string; text: string }) {
  return <div style={{ marginTop: 7, lineHeight: 1.45 }}><b>{label}:</b> <span style={{ color: "var(--muted)" }}>{text}</span></div>;
}

function MiniKpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "good" | "warn" | "bad" }) {
  const color = tone === "good" ? "var(--pass)" : tone === "bad" ? "var(--fail)" : tone === "warn" ? "var(--brass-deep)" : undefined;
  return <div className="card kpi"><div className="kl">{label}</div><div className="kv" style={{ color, fontSize: 20 }}>{value}</div><div className="ks">{sub}</div></div>;
}
